import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, HelpCircle, Construction, UserCheck } from 'lucide-react';
import { globalEventBus, EVENTS } from '../services/eventBus';
import { extractEntitiesFromText } from '../services/geminiService';
import { ontologyManager } from '../services/ontologyManager';
import { useOntologyStore } from '@/stores/useOntologyStore';
import type { IdentificationResult } from '../services/ontologyManager';
import type { GraphData, GraphNode } from '../types';
import { SourcePanel } from '@/components/source-panel/SourcePanel';

export function SmartEditorPanel() {
  const concepts = useOntologyStore((state) => state.concepts);
  const [text, setText] = useState(
    'We are deploying React and Docker for the new Marketing Campaign using Google Ads.'
  );
  const [analyzedEntities, setAnalyzedEntities] = useState<
    { word: string; result: IdentificationResult }[]
  >([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textRef = useRef(text);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => {
    void handleProcess(text);
  }, []);

  const handleProcess = async (inputText: string) => {
    if (!inputText || inputText.trim().length === 0) {
      setAnalyzedEntities([]);
      globalEventBus.emit(EVENTS.ENTITIES_DETECTED, { sourceId: 'editor', entities: [] });
      globalEventBus.emit(EVENTS.GRAPH_UPDATE, { nodes: [], links: [] } as GraphData);
      return;
    }
    const rawEntities = await extractEntitiesFromText(inputText);
    const analyzed = rawEntities.map((word) => ({
      word,
      result: ontologyManager.identifyWord(word, concepts),
    }));
    setAnalyzedEntities(analyzed);

    const nodes: GraphNode[] = analyzed.map((item) => {
      const layer = item.result.layer;
      const group = layer === 'CORE'
        ? 'core'
        : layer === 'CUSTOMER'
          ? 'customer'
          : layer === 'DRAFT'
            ? 'draft'
            : 'unknown';
      return {
        id: item.word,
        label: item.word,
        group,
        status: layer,
        type: item.result.term?.type ?? 'CONCEPT',
        val: layer === 'CORE' ? 18 : layer === 'CUSTOMER' ? 14 : 10,
      };
    });

    // Ontology-aware links: attach everything to a CORE hub when available
    const links: { source: string; target: string }[] = [];
    const coreNodes = nodes.filter((node) => node.group === 'core');
    const hub = coreNodes.sort((a, b) => a.label.localeCompare(b.label))[0];

    if (hub) {
      nodes.forEach((node) => {
        if (node.id === hub.id) {
          return;
        }
        links.push({
          source: hub.id,
          target: node.id,
        });
      });
    }

    globalEventBus.emit(EVENTS.ENTITIES_DETECTED, { sourceId: 'editor', entities: analyzed });
    globalEventBus.emit(EVENTS.GRAPH_UPDATE, { nodes, links } as GraphData);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setText(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      void handleProcess(value);
    }, 700);
  };

  const getStatusColor = (layer: string) => {
    switch (layer) {
      case 'CORE':
        return 'bg-sopho-entity/15 text-sopho-entity border-sopho-entity/40';
      case 'CUSTOMER':
        return 'bg-sopho-process/15 text-sopho-process border-sopho-process/40';
      case 'DRAFT':
        return 'bg-sopho-rule/15 text-sopho-rule border-sopho-rule/40';
      default:
        return 'bg-destructive/15 text-destructive border-destructive/40';
    }
  };

  const getStatusIcon = (layer: string) => {
    switch (layer) {
      case 'CORE':
        return <ShieldCheck size={12} />;
      case 'CUSTOMER':
        return <UserCheck size={12} />;
      case 'DRAFT':
        return <Construction size={12} />;
      default:
        return <HelpCircle size={12} />;
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="p-3 border-b border-border">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Smart Editor
        </h3>
      </div>
      <div className="p-3 border-b border-border">
        <SourcePanel />
      </div>
      <div className="relative flex-1 p-3">
        <textarea
          className="w-full h-full bg-transparent text-foreground resize-none outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded font-mono text-sm leading-relaxed"
          value={text}
          onChange={handleChange}
          placeholder="Tippe, um Wissen zu extrahieren…"
          spellCheck={false}
        />
      </div>
      <div className="bg-muted/30 backdrop-blur-sm rounded-lg px-3 py-2 border-t border-border">
        <p className="text-[10px] uppercase font-bold text-muted-foreground mb-2">Erkannte Konzepte</p>
        <div className="flex flex-wrap gap-2 min-h-[24px]">
          {analyzedEntities.map((item, idx) => (
            <div
              key={idx}
              title={`${item.word}: ${item.result.layer}`}
              className={`text-[10px] px-2 py-1 rounded border flex items-center gap-1.5 transition-all cursor-help ${getStatusColor(
                item.result.layer
              )}`}
            >
              {getStatusIcon(item.result.layer)}
              <span className="font-mono font-medium">{item.word}</span>
            </div>
          ))}
          {analyzedEntities.length === 0 && (
            <span className="text-[10px] text-muted-foreground italic">Noch keine Entitäten erkannt.</span>
          )}
        </div>
      </div>
    </div>
  );
}
